import {DateTimeFormatter, LocalDate, LocalTime} from '@js-joda/core';
import {Locale} from '@js-joda/locale_en-us';
import Hidden from '@material-ui/core/Hidden';
import IconButton from '@material-ui/core/IconButton';
import makeStyles from '@material-ui/core/styles/makeStyles';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Tooltip from '@material-ui/core/Tooltip';
import AddIcon from '@material-ui/icons/Add';
import InsertAboveIcon from '@material-ui/icons/ExpandMore';
import React, {useCallback, useMemo, useState} from 'react';
import {TimeEntry} from '../../pages/TimeEntries';
import {DateRange} from './DateRangeSelect';
import TimeEntryFormDialog from './TimeEntryFormDialog';

const useStyles = makeStyles(theme => ({
    root: {
        backgroundColor: theme.palette.primary.light,
        '& td': {
            color: theme.palette.primary.contrastText,
            fontWeight: 500,
        },
        '& button': {
            color: theme.palette.primary.contrastText,
        },
    },
    header: {
        cursor: 'pointer',
    },
}));

type Props = {
    date : LocalDate;
    timeEntries : TimeEntry[];
    onDateRangeChange : (dateRange : DateRange) => void;
    onInsert : (timeEntry : TimeEntry) => Promise<void>;
};

const dateFormatter = DateTimeFormatter.ofPattern('EEEE M/d').withLocale(Locale.US);

const TimeEntryDateHeader : React.FC<Props> = ({date, onDateRangeChange, timeEntries, onInsert} : Props) => {
    const classes = useStyles();
    const [formDialogOpenState, setFormDialogOpenState] = useState<null | 'create' | 'prepend'>(null);

    const initialCreateValues : Partial<TimeEntry> = useMemo(() => ({
        startDate: date,
    }), [date]);

    const initialPrependValues : Partial<TimeEntry> | undefined = useMemo(() => {
        let earliestUsableTime;

        for (const timeEntry of timeEntries) {
            if (!timeEntry.startTime) {
                continue;
            }

            if (timeEntry.startTime.equals(LocalTime.MIDNIGHT)) {
                return;
            }

            if (!earliestUsableTime || earliestUsableTime > timeEntry.startTime) {
                earliestUsableTime = timeEntry.startTime;
            }
        }

        if (!earliestUsableTime) {
            return;
        }

        return {
            startDate: date,
            endTime: earliestUsableTime,
        };
    }, [date, timeEntries]);

    const handleSubmit = useCallback(async (timeEntry) => {
        setFormDialogOpenState(null);
        return onInsert(timeEntry);
    }, [setFormDialogOpenState, onInsert]);

    return (
        <TableRow className={classes.root}>
            <TableCell>
                <span className={classes.header} onClick={() => onDateRangeChange({start: date, end: date})}>
                    {date.format(dateFormatter)}
                </span>
            </TableCell>
            <Hidden smDown>
                <TableCell/>
                <TableCell/>
                <TableCell/>
            </Hidden>
            <TableCell colSpan={2}/>
            <TableCell>
                <Tooltip title="Create Time Entry">
                    <IconButton onClick={() => setFormDialogOpenState('create')} size="small"><AddIcon/></IconButton>
                </Tooltip>
                {initialPrependValues && (
                    <Tooltip title="Insert above">
                        <IconButton onClick={() => setFormDialogOpenState('prepend')} size="small">
                            <InsertAboveIcon/>
                        </IconButton>
                    </Tooltip>
                )}
                <TimeEntryFormDialog
                    timeEntry={formDialogOpenState === 'prepend' ? initialPrependValues : initialCreateValues}
                    open={formDialogOpenState !== null}
                    onClose={() => setFormDialogOpenState(null)}
                    onSubmit={handleSubmit}
                />
            </TableCell>
        </TableRow>
    );
};

export default TimeEntryDateHeader;
